function uniqueTrimmed(values) {
    const seen = new Set();
    const out = [];
    for (const value of Array.isArray(values) ? values : []) {
        const label = String(value || '').trim();
        const key = label.toLowerCase();
        if (!label || seen.has(key)) continue;
        seen.add(key);
        out.push(label);
    }
    return out;
}

function normalizeSportKey(sport) {
    return String(sport || '').trim().toLowerCase();
}

function sportsMatch(left, right) {
    const a = normalizeSportKey(left);
    const b = normalizeSportKey(right);
    return Boolean(a && b && a === b);
}

function isUnspecifiedOrAllSport(sport) {
    const key = normalizeSportKey(sport);
    return !key || key === 'all' || key === 'general' || key === 'general resources';
}

function findCurrentOrgInstructor(organization, user) {
    const instructors = Array.isArray(organization?.instructors) ? organization.instructors : [];
    const userId = user?.id != null ? String(user.id) : '';
    const email = String(user?.email || '').trim().toLowerCase();
    if (userId) {
        const byId = instructors.find((instructor) => instructor && String(instructor.id) === userId);
        if (byId) return byId;
    }
    if (!email) return null;
    return instructors.find((instructor) => (
        String(instructor?.email || '').trim().toLowerCase() === email
    )) || null;
}

function instructorHasLimitedSports(instructor) {
    if (!instructor) return false;
    return instructor.limitToTaggedSports === true;
}

function getInstructorAllowedSports(instructor, orgSports) {
    const orgList = uniqueTrimmed(orgSports);
    if (!instructor) return orgList;
    const tagged = uniqueTrimmed(instructor.sports);
    if (!tagged.length) return [];
    if (instructor.limitToTaggedSports !== true) return orgList;
    if (!orgList.length) return tagged;
    return tagged
        .map((sport) => orgList.find((orgSport) => sportsMatch(orgSport, sport)))
        .filter(Boolean);
}

function buildCourseSportLookup(courses = []) {
    const lookup = {};
    for (const item of Array.isArray(courses) ? courses : []) {
        if (!item) continue;
        const sport = item.sport || item.sportName || '';
        if (item.id != null && item.id !== '') lookup[String(item.id)] = sport;
        if (item.name) lookup[`name:${normalizeSportKey(item.name)}`] = sport;
    }
    return lookup;
}

function uniqueSportsFromItems(items = []) {
    return uniqueTrimmed(
        (Array.isArray(items) ? items : []).map((item) => item?.sport || item?.sportName)
    );
}

function collectLinkedCourseIds(item = {}) {
    const values = [
        item.sportCourseId,
        ...(Array.isArray(item.sportCourseIds) ? item.sportCourseIds : []),
        ...(Array.isArray(item.requiredCourseCompletions) ? item.requiredCourseCompletions : []),
        ...(Array.isArray(item.requirements) ? item.requirements : []),
        ...(Array.isArray(item.sport_ids) ? item.sport_ids : []),
    ];
    return uniqueTrimmed(values.map((value) => {
        if (value == null) return '';
        if (typeof value === 'object') return value.id || value.courseId || value.sportCourseId || '';
        return value;
    }));
}

function collectLinkedCourseNames(item = {}) {
    return uniqueTrimmed([
        item.sportCourse,
        ...(Array.isArray(item.sportCourses) ? item.sportCourses : []),
    ]);
}

function collectItemSports(item = {}, courseLookup = {}) {
    const direct = uniqueTrimmed([
        item?.sport,
        item?.sportName,
        item?.category,
        ...(Array.isArray(item?.sports) ? item.sports : []),
        ...(Array.isArray(item?.categories) ? item.categories : []),
    ]);
    const inferred = [];
    collectLinkedCourseIds(item).forEach((id) => {
        if (courseLookup[id]) inferred.push(courseLookup[id]);
    });
    collectLinkedCourseNames(item).forEach((name) => {
        const sport = courseLookup[`name:${normalizeSportKey(name)}`];
        if (sport) inferred.push(sport);
    });
    return uniqueTrimmed([...direct, ...inferred]);
}

function contentSportIsAllowed(item, allowedSports, options = {}) {
    const allowed = uniqueTrimmed(allowedSports);
    const allowUnspecified = options.allowUnspecified === true;
    const sports = collectItemSports(item, options.courseLookup || {});
    const explicit = sports.filter((sport) => !isUnspecifiedOrAllSport(sport));
    if (!explicit.length) return allowUnspecified && allowed.length > 0;
    if (!allowed.length) return false;
    return explicit.some((sport) => allowed.some((allowedSport) => sportsMatch(sport, allowedSport)));
}

function filterItemsByInstructorSports(items, allowedSports, options = {}) {
    return (Array.isArray(items) ? items : []).filter((item) => (
        contentSportIsAllowed(item, allowedSports, options)
    ));
}

function filterSportDropdownOptions(sports, allowedSports) {
    const allowed = uniqueTrimmed(allowedSports);
    return uniqueTrimmed(sports).filter((sport) => (
        allowed.some((allowedSport) => sportsMatch(sport, allowedSport))
    ));
}

function resolveInstructorSportAccess(organization, user, orgSports) {
    const instructor = findCurrentOrgInstructor(organization, user);
    return {
        instructor,
        limited: instructorHasLimitedSports(instructor),
        allowedSports: getInstructorAllowedSports(instructor, orgSports),
    };
}

export {
    uniqueTrimmed,
    normalizeSportKey,
    sportsMatch,
    isUnspecifiedOrAllSport,
    findCurrentOrgInstructor,
    instructorHasLimitedSports,
    getInstructorAllowedSports,
    buildCourseSportLookup,
    uniqueSportsFromItems,
    collectItemSports,
    contentSportIsAllowed,
    filterItemsByInstructorSports,
    filterSportDropdownOptions,
    resolveInstructorSportAccess,
};
